import React, { useEffect, useCallback, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useHistory, useParams } from 'react-router-dom';
import {
  FormCard,
  YupNumeric,
  YupDate,
  YupString,
  YupBoolean,
  promiseModalDialog,
  isNumeric,
  toastSuccess,
} from 'motion-components';
import * as Yup from 'yup';
import * as Lo from 'lodash';
import { gqlGetByIdQuery, gqlAdd, gqlUpdate, gqlDelete, FormInput } from '../graphql';
import '../../../../styles/tab-styles.css';
import { ItransFormFields } from './itrans-form-fields';
import { useRootStore } from 'store';
import { useHandleError } from 'shared-pages/services';
import { useTranslation } from 'react-i18next';
import { ButtonCloseModal, RowFieldTooltips } from 'shared-pages/components';
import { convertFieldToInt } from 'shared-pages/common/form-helpfunc';
import { IdocWasteOriginTypes } from 'shared-pages/shared/constants';

type Props = {
  isModal?: boolean;
  paramId?: string | undefined;
  modalOnClose?: (id: number) => void | undefined;
};

export const InternalTransactionFormPage = ({
  isModal = false,
  paramId = undefined,
  modalOnClose,
}: Props) => {
  const { handleGraphqlError } = useHandleError('collector');
  const { appStore } = useRootStore();
  const history = useHistory();
  const { id: urlId } = useParams<{ id: string }>();
  const id = isModal ? paramId : urlId;
  const idNum = isNumeric(id) ? parseInt(id) : -1;
  const isNew = idNum < 0;
  const { t } = useTranslation();

  const formShape = {
    id: YupNumeric,
    code: YupString,
    seriesId: YupNumeric.nullable(), // Allow null
    codePrefix: YupString.nullable(), // Allow null
    codeNumber: YupNumeric.nullable(), // Allow null
    // collectorId: YupNumeric.required(t('Ο συλλέκτης είναι υποχρεωτικός')),
    // collector: Yup.object().shape({
    //   id: YupNumeric,
    //   name: YupString,
    // }),

    notes: YupString,
    transactionDate: YupDate,
    wasteOriginTypeId: YupNumeric.required(t('Ο τύπος προέλευσης είναι υποχρεωτικός')),
    fromCollectionpointId: YupNumeric.when('wasteOriginTypeId', {
      is: IdocWasteOriginTypes.PrimaryStorage,
      then: YupNumeric.required(t('Η προέλευση είναι υποχρεωτική')),
    }),
    fromWastereceptionpointId: YupNumeric.when('wasteOriginTypeId', {
      is: IdocWasteOriginTypes.SecondaryStorage,
      then: YupNumeric.required(t('Η προέλευση είναι υποχρεωτική')),
    }),

    toWastereceptionpointId: YupNumeric.required(t('Ο προορισμός είναι υποχρεωτικός')).test(
      'different-from-wastereceptionpoint',
      t('Η προέλευση και ο προορισμός δεν μπορούν να είναι ίδιοι'),
      function (value) {
        const { fromWastereceptionpointId } = this.parent;
        if (!fromWastereceptionpointId || !value) return true;
        return fromWastereceptionpointId !== value;
      },
    ),
    vehicleId: YupNumeric,
    driverId: YupNumeric,
    isActive: YupBoolean,
    fromCollectionpoint: Yup.object()
      .shape({
        id: YupNumeric,
        name: YupString,
      })
      .nullable(),
    fromWastereceptionpoint: Yup.object()
      .shape({
        id: YupNumeric,
        code: YupString.nullable(),
        name: YupString,
      })
      .nullable(),
    toWastereceptionpoint: Yup.object().nullable().shape({
      id: YupNumeric,
      code: YupString.nullable(),
      name: YupString,
    }),
    internalTransactionLines: Yup.array()
      .of(
        Yup.object().shape({
          id: YupNumeric.nullable(), // Allow null for optional fields
          aa: YupNumeric.nullable(),
          materialId: YupNumeric,
          qty: YupNumeric,
          netWeight: YupNumeric.test(
            'netweight-not-zero',
            t('Το βάρος πρέπει να είναι διάφορο του μηδενός'),
            function (value) {
              if (value === null || value === undefined || value === '') return true;
              const numericValue = typeof value === 'number' ? value : parseFloat(value as any);
              if (Number.isNaN(numericValue)) return true;
              return numericValue !== 0;
            },
          ),
          material: Yup.object().shape({
            id: YupNumeric,
            name: YupString,
          }),
        }),
      )
      .min(1, t('Πρέπει να δηλώσετε τουλάχιστον ένα απόβλητο')), // Allow null if the entire array is optional
  };

  const validationSchema = Yup.object().shape(formShape);
  const pickProperties = (values) => Lo.pick(values, Lo.keys(formShape));
  type FormValuesType = Yup.InferType<typeof validationSchema>;
  const router = useHistory();
  const [addNewRecord] = useMutation(gqlAdd);
  const [updateRecord] = useMutation(gqlUpdate);
  const [deleteRecord] = useMutation(gqlDelete);
  const [get, { data: rawData, error, refetch }] = useLazyQuery(gqlGetByIdQuery);
  const data = rawData ? rawData[Object.keys(rawData)[0]] : undefined;
  const [initialNewRecData, setInitialNewRecData] = useState<any>();

  const initializeFromCopy = useCallback(() => {
    const copyFrom = (history.location?.state as { copyFrom?: any })?.copyFrom;
    if (!copyFrom) return;
    // Copy all fields except id, code, etc.
    let newRec: any = {
      ...copyFrom,
      id: undefined,
      code: '',
      codePrefix: '',
      codeNumber: undefined,
      transactionDate: new Date(),
      isActive: true,
    };
    // Remove ids from lines
    if (Array.isArray(newRec.internalTransactionLines)) {
      newRec.internalTransactionLines = newRec.internalTransactionLines.map((line) => ({
        ...line,
        id: undefined,
      }));
    }
    setInitialNewRecData(newRec);
  }, [history]);

  /** Fetch data or Initialize new record */
  useEffect(() => {
    if (isNew) {
      // If copyFrom exists, initialize from copy
      const state = history.location?.state as { copyFrom?: any } | undefined;
      if (state?.copyFrom) {
        initializeFromCopy();
      } else {
        initializeNewRecord();
      }
    } else {
      // fetch data
      get({ variables: { id: idNum } });
    }
  }, [get, id, idNum, isNew, initializeFromCopy]);

  /** Αρχικοποίησε Νέα Εγγραφή  */
  const initializeNewRecord = async () => {
    let newRec: any = {
      isActive: true,
      wasteOriginTypeId: IdocWasteOriginTypes.SecondaryStorage,
      collectorId: appStore.isCollector ? appStore.associatedId : null,
      transactionDate: new Date(),
      internalTransactionLines: [],
    };
    setInitialNewRecData(newRec);
  };

  /**
   * * ΑΠΟΘΗΚΕΥΣΗ - UPDATE/CREATE
   */
  const handleSubmit = async (values: FormValuesType, actions) => {
    actions.setSubmitting(false);
    const mutValues: any = Lo.pick(values, Lo.keys(new FormInput()));
    convertFieldToInt(mutValues, ['seriesId', 'codeNumber']);
    //clean up _typename and material in internalTransactionLines parseInt,parseFloat for numeric fields

    mutValues.internalTransactionLines = mutValues.internalTransactionLines.map((line) => {
      const newLine = { ...line };
      newLine.containersQty = parseFloat(line.containersQty);
      newLine.netWeight = parseFloat(line.netWeight);
      newLine.aa = parseInt(line.aa);
      return Lo.omit(newLine, ['material', 'idocs', '__typename']);
    });

    // console.log('mutValues', mutValues);
    // Αν είναι νέα εγγραφή τοτε γράφω όλα τα πεδία
    if (isNew) {
      const rec = await addNewRecord({ variables: { data: { ...mutValues, id: idNum } } })
        .then((d) => {
          const rec = d.data[Object.keys(d.data)[0]];
          toastSuccess(`Επιτυχημένη Προσθήκη Εγγραφής [${rec?.id}]`);
          router.push(`/itran/${rec.id}`);
          return rec;
          //navigate to new record
        })
        .catch((error) => {
          handleGraphqlError('mutation-add', error);
          return null;
        });
      if (rec) {
        if (isModal) {
          if (modalOnClose) modalOnClose(rec.id);
        }
      }
    } else {
      // Αν είναι update εγγραφής τοτε γράφω μονο τα αλλαγμένα πεδία
      console.log('[Applog] Update:', mutValues);
      updateRecord({ variables: { data: { ...mutValues, id: idNum } } })
        .then((d) => {
          toastSuccess(`Επιτυχής Ενημέρωση Εγγραφής [${d.data[Object.keys(d.data)[0]].id}]`);
          refetch();
        })
        .catch((error) => {
          handleGraphqlError('mutation-update', error);
        });
    }
  };

  /**
   * *  DELETE
   */
  const handleDelete = useCallback(async () => {
    const res = await promiseModalDialog({
      title: 'Διαγραφή Εγγραφής',
      text: 'Είστε σίγουροι ότι θέλετε να διαγράψετε την Εγγραφή;',
    });
    if (res) {
      deleteRecord({ variables: { id: idNum } })
        .then(() => history.goBack())
        .catch((error) => {
          handleGraphqlError('mutation-delete', error);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteRecord, idNum]);

  if (error) {
    handleGraphqlError('query-get', error);
    return <h3 className="text-center">{error.message}</h3>;
  } else {
    return (
      <>
        <FormCard
          alwaysOn={isNew} // OK Btn Visible
          title={
            isNew
              ? `${t('Δήλωση Νέας Εσωτερικής Διακίνησης')}`
              : `${t('Εσωτερική Διακίνηση')}: ${data?.code}`
          }
          onSubmit={handleSubmit}
          onDelete={handleDelete}
          onReturn={null}
          permitSubmit={false}
          permitDelete={!isNew && appStore.isPlatformOwner}
          initialValues={isNew ? initialNewRecData : pickProperties(data)}
          validationSchema={validationSchema}
          enableReinitialize
          idx={idNum}
          injectFooterElements={
            !isModal
              ? undefined
              : (formikCtx) => {
                  return (
                    <ButtonCloseModal
                      onClick={() => {
                        modalOnClose(-1);
                      }}
                    />
                  );
                }
          }
        >
          <ItransFormFields
            isNew={isNew}
            updateRecord={updateRecord}
            refetch={refetch}
            data={data}
          />
          {appStore.isPlatformOwner && <RowFieldTooltips data={data} />}
        </FormCard>
      </>
    );
  }
};
